import React from 'react';
import { Users, Award, Globe, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import Site from '../data/siteConfig.json';
import AdSlot from '../components/AdSlot';

const About: React.FC = () => {
  const stats = [
    { icon: Users, value: '500+', label: 'Happy Clients' },
    { icon: Award, value: '5+', label: 'Years Experience' },
    { icon: Globe, value: '18+', label: 'Countries Served' },
    { icon: Heart, value: '370+', label: 'Projects Completed' },
  ];

  const team = [
    { name: 'John Smith', role: 'Founder & CEO', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80' },
    { name: 'Sarah Johnson', role: 'Lead Designer', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80' },
    { name: 'Mike Williams', role: 'Senior Developer', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80' },
    { name: 'Emily Davis', role: 'Marketing Director', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80' },
  ];

  return (
    <>
      <SEO
        title="About Us"
        description="Learn about TheCodeBazaar, our mission, values, and the team behind our success."
        keywords="about thecodebazaar, web services india"
        schemaType="page"
      />

      <div className="bg-gradient-to-br from-sky-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            About {Site.name}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're passionate about creating beautiful, high-performance websites that help businesses succeed online.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p className="text-lg">
                Founded in 2021, {Site.name} started with a simple mission: to make professional web design and development accessible to businesses of all sizes. What began as a small team of passionate developers has grown into a full-service digital agency serving clients worldwide.
              </p>
              <p className="text-lg">
                Over the years, we've helped thousands of businesses establish their online presence through custom websites, premium themes, and comprehensive digital marketing strategies. Our commitment to quality, innovation, and customer satisfaction has made us a trusted partner for businesses looking to succeed online.
              </p>
              <p className="text-lg">
                Today, we continue to push the boundaries of web design and development, staying ahead of trends and technologies to deliver cutting-edge solutions that drive real results for our clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AdSlot slot="heroBanner2" />

      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">By the Numbers</h2>
            <p className="text-lg text-gray-600">Our impact in the digital world</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm text-center group hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8 text-sky-500" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="hidden py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-lg text-gray-600">The talented people behind TheCodeBazaar</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl mb-4">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full aspect-square object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-gray-600">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-sky-500 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-12">
            <div>
              <h3 className="text-2xl font-semibold mb-3">Quality First</h3>
              <p className="text-sky-50">
                We never compromise on quality. Every project receives our full attention and expertise.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-3">Customer Success</h3>
              <p className="text-sky-50">
                Your success is our success. We're committed to delivering results that exceed expectations.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-3">Innovation</h3>
              <p className="text-sky-50">
                We stay ahead of trends and technologies to provide cutting-edge solutions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;